import {
  Box,
  createStyles,
  Select,
  Text,
  TextInput,
  Drawer,
  Loader,
} from "@mantine/core";
import { useEffect, useState, useRef, useCallback } from "react";
import { IconChevronDown } from "@tabler/icons-react";
import { LuSearch, LuTrash2 } from "react-icons/lu";
import { useSelector } from "react-redux";
import { useForm } from "@mantine/form";
import swal from "sweetalert";
import {
  createBillingProvider,
  editProvider,
  getBillingProvider,
} from "../../services/billingprovider";
import { stateZipCodes } from "../../utils/zipCodes";
import { debounce } from "../../utils";
import { getProviderServices } from "../../services/servicecode";
import { setAllProviderServices } from "../../store/reducers/servicecode";
import store from "../../store";
import ProviderServicesLookupModal from "./providerServicesLookupModal";
import ProviderServiceSearch from "./ProviderServiceSearch";
import { searchAddress, getAddressByPlaceId } from "../../services/dashboard";

const useStyles = createStyles(() => ({
  drawerBody: {
    padding: 0,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    background: "var(--app-surface)",
  },
  drawerContent: {
    display: "flex",
    flexDirection: "column",
    background: "var(--app-surface)",
    borderRadius: "16px",
    border: "1px solid var(--app-border)",
    margin: "16px",
    height: "calc(100vh - 32px)",
    maxHeight: "calc(100vh - 32px)",
    overflow: "hidden",
    boxShadow: "-20px 0 48px rgba(15, 23, 42, 0.18)",
  },
  modalHead: {
    padding: "22px 28px",
    borderBottom: "1px solid var(--app-border)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    flexShrink: 0,
  },
  modalEyebrow: {
    margin: "0 0 4px",
    fontSize: "11.5px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "var(--app-text-muted)",
  },
  modalTitle: {
    margin: 0,
    fontSize: "22px",
    fontWeight: 700,
    color: "var(--app-text)",
    letterSpacing: "-0.018em",
  },
  closeBtn: {
    width: "36px",
    height: "36px",
    borderRadius: "8px",
    background: "var(--app-surface-2)",
    border: "none",
    display: "grid",
    placeItems: "center",
    cursor: "pointer",
    color: "var(--app-text)",
    flexShrink: 0,
    "&:hover": { background: "var(--app-surface-2)" },
    "& svg": { width: "18px", height: "18px" },
  },
  modalBody: {
    padding: "22px 28px",
    overflowY: "auto",
    flex: 1,
    minHeight: 0,
  },
  sectionH6: {
    margin: "0 0 12px",
    fontSize: "11.5px",
    fontWeight: 700,
    color: "var(--app-text-muted)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  sectionH6Top: {
    margin: "22px 0 12px",
    fontSize: "11.5px",
    fontWeight: 700,
    color: "var(--app-text-muted)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  sectionServiceRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
    marginTop: "28px",
    marginBottom: "12px",
  },
  sectionServiceLeft: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  sectionH6Inline: {
    margin: 0,
    fontSize: "11.5px",
    fontWeight: 700,
    color: "var(--app-text)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  sectionDescInline: {
    margin: 0,
    fontSize: "12.5px",
    color: "var(--app-text-muted)",
    lineHeight: 1.5,
  },
  lookupBtn: {
    height: "34px",
    padding: "0 14px",
    borderRadius: "8px",
    border: "1px solid var(--app-border)",
    background: "var(--app-surface)",
    color: "var(--app-text)",
    fontSize: "12.5px",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    whiteSpace: "nowrap",
    flexShrink: 0,
    lineHeight: 1,
    "&:hover": { background: "var(--app-surface-2)" },
  },
  formGrid2: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "16px 18px",
  },
  formGrid1: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "16px",
  },
  formGrid3: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "16px 18px",
    marginTop: "14px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  fieldLabel: {
    fontSize: "12.5px",
    fontWeight: 600,
    color: "var(--app-text)",
  },
  fieldReq: {
    color: "#DC2626",
    marginLeft: "2px",
  },
  styledInput: {
    "& input": {
      height: "42px",
      padding: "0 12px",
      border: "1px solid var(--app-border)",
      borderRadius: "8px",
      fontSize: "13px",
      color: "var(--app-text)",
      fontFamily: "Inter, inherit",
      "&:focus": {
        borderColor: "var(--app-text)",
        boxShadow: "0 0 0 3px rgba(23,23,23,0.08)",
      },
    },
    "& .mantine-TextInput-wrapper": { width: "100%" },
  },
  styledSelect: {
    "& .mantine-Select-input": {
      height: "42px",
      border: "1px solid var(--app-border)",
      borderRadius: "8px",
      fontSize: "13px",
      paddingLeft: "12px",
      color: "var(--app-text)",
      fontFamily: "Inter, inherit",
      "&:focus": {
        borderColor: "var(--app-text)",
        boxShadow: "0 0 0 3px rgba(23,23,23,0.08)",
      },
    },
    "& .mantine-Select-wrapper": { width: "100%" },
  },
  inlineBox: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    border: "1px solid var(--app-border)",
    borderRadius: "8px",
    padding: "0 12px",
    backgroundColor: "var(--app-surface)",
    height: "42px",
  },
  inlineSelect: {
    width: "68px",
    minWidth: "68px",
    flexShrink: 0,
    "& .mantine-Select-input": {
      fontSize: "13px",
      color: "var(--app-text)",
      border: "none",
      padding: 0,
      height: "38px",
      minHeight: "unset",
    },
    "& .mantine-Select-wrapper": { width: "68px" },
  },
  inlineInput: {
    flex: 1,
    "& input": {
      fontSize: "13px",
      border: "none",
      padding: 0,
      height: "38px",
      minHeight: "unset",
    },
  },
  addCodeBtn: {
    width: "100%",
    height: "40px",
    background: "var(--app-primary)",
    color: "var(--app-on-primary)",
    border: "none",
    borderRadius: "8px",
    fontSize: "12.5px",
    fontWeight: 600,
    cursor: "pointer",
    marginTop: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    "&:hover": { background: "var(--app-primary)" },
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  tableWrap: {
    border: "1px solid var(--app-border)",
    borderRadius: "8px",
    overflow: "hidden",
    marginTop: "18px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "13px",
    "& thead tr": { background: "var(--app-surface-2)" },
    "& thead th": {
      fontSize: "11px",
      fontWeight: 600,
      color: "var(--app-text-muted)",
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      padding: "10px 12px",
      textAlign: "left",
      borderBottom: "1px solid var(--app-border)",
    },
    "& tbody tr": {
      borderBottom: "1px solid var(--app-border)",
      "&:last-child": { borderBottom: "none" },
    },
    "& tbody td": {
      padding: "10px 12px",
      fontSize: "13px",
      color: "var(--app-text)",
      verticalAlign: "middle",
    },
  },
  tableDeleteCell: {
    textAlign: "right",
  },
  tableInput: {
    height: "30px",
    padding: "0 8px",
    border: "1px solid var(--app-border)",
    borderRadius: "6px",
    fontSize: "13px",
    color: "var(--app-text)",
    width: "100%",
    background: "var(--app-surface)",
    "&:focus": {
      outline: "none",
      borderColor: "var(--app-text)",
      boxShadow: "0 0 0 2px rgba(23,23,23,0.08)",
    },
  },
  deleteBtn: {
    width: "30px",
    height: "30px",
    borderRadius: "6px",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--app-text-muted)",
    padding: 0,
    "&:hover": { background: "var(--app-tint-red)", color: "#B91C1C" },
  },
  modalFoot: {
    padding: "16px 28px 22px",
    borderTop: "1px solid var(--app-border)",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "10px",
    flexShrink: 0,
  },
  cancelBtn: {
    height: "40px",
    padding: "0 16px",
    border: "1px solid var(--app-border)",
    borderRadius: "8px",
    background: "transparent",
    color: "var(--app-text)",
    fontSize: "13.5px",
    fontWeight: 600,
    cursor: "pointer",
    "&:hover": { background: "var(--app-surface-2)" },
  },
  saveBtn: {
    height: "40px",
    padding: "0 16px",
    borderRadius: "8px",
    background: "var(--app-primary)",
    color: "var(--app-on-primary)",
    fontSize: "13.5px",
    fontWeight: 600,
    border: "none",
    cursor: "pointer",
    "&:hover": { background: "var(--app-primary)" },
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
}));

const FieldInput = ({ label, required, children }) => {
  const { classes } = useStyles();
  return (
    <Box className={classes.field}>
      {label && (
        <Text component="label" className={classes.fieldLabel}>
          {label}
          {required && <span className={classes.fieldReq}>*</span>}
        </Text>
      )}
      {children}
    </Box>
  );
};

const StyledInput = ({ ...rest }) => {
  const { classes } = useStyles();
  return (
    <TextInput variant="unstyled" className={classes.styledInput} {...rest} />
  );
};

const StyledSelect = ({ ...rest }) => {
  const { classes } = useStyles();
  return (
    <Select
      className={classes.styledSelect}
      rightSection={<IconChevronDown color="var(--app-text-muted)" size={16} />}
      rightSectionWidth={28}
      styles={{ rightSection: { pointerEvents: "none" } }}
      {...rest}
    />
  );
};

const TAXONOMY_OPTIONS = [
  { value: "251E00000X", label: "251E00000X. Home Health" },
  { value: "253Z00000X", label: "253Z00000X. In Home Supportive Care" },
  { value: "261QH0700X", label: "261QH0700X. Health Service" },
  { value: "163WH0500X", label: "163WH0500X. Registered Nurse, Home Health" },
];

const UNIT_OPTIONS = [
  { value: "15 min", label: "15 min" },
  { value: "30 min", label: "30 min" },
  { value: "45 min", label: "45 min" },
  { value: "1 hour", label: "1 hour" },
  { value: "1 day", label: "1 day" },
  { value: "1 visit", label: "1 visit" },
  { value: "1 unit", label: "1 unit" },
];

const EMPTY_SERVICE = { servicenumber: "", servicetitle: "", servicechargetype: "", servicechargeNumber: "", servicechargeAmount: "", mod1: "" };

const FIELD_LABELS = {
  name: "Name",
  phone: "Phone",
  taxonomy: "Taxonomy",
  address: "Address",
  city: "City",
  state: "State",
  zipcode: "Zip Code",
  billingIdentifierType: "Billing Identifier Type",
  billingIdentifier: "Billing Identifier",
  providertype: "Provider Type",
};

// add mode:  state/setState props
// edit mode: editprovider/setEditProvider + item + isPreview props
function AddProvider({
  state,
  setState,
  returnedFacility,
  editprovider,
  setEditProvider,
  item,
  isPreview,
  onSaved,
}) {
  console.log("🚀 ~ AddProvider ~ item:", item)
  const { classes } = useStyles();

  const isEdit = editprovider !== undefined;
  const opened = isEdit ? editprovider : state?.addProvider;

  const { profile } = useSelector((s) => s?.profile);
  const allfacilty = useSelector((s) => s.facility);
  const { allservicecode, allproviderservices } = useSelector((s) => s?.servicecode);
  const allProviderServicesList = allproviderservices?.allproviderservices;

  const [serviceSearch, setServiceSearch] = useState("");
  const [selectedCode, setSelectedCode] = useState("");
  const [provider, setProvider] = useState(null);
  const [services, setServices] = useState([]);
  const [serviceField, setServiceField] = useState(EMPTY_SERVICE);
  const [isEditMode, setIsEditMode] = useState(false);
  const [openlookup, setOpenLookup] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const debounceRef = useRef(null);
  const [addr2Suggestions, setAddr2Suggestions] = useState([]);
  const [showSuggestions2, setShowSuggestions2] = useState(false);
  const [addr2Loading, setAddr2Loading] = useState(false);
  const debounceRef2 = useRef(null);

  const form = useForm({
    initialValues: {
      name: "",
      phone: "",
      taxonomy: "",
      address: "",
      address2: "",
      city: "",
      state: "",
      zipcode: "",
      federaltaxtype: "",
      federaltaxNumber: "",
      billingIdentifierType: "",
      billingIdentifier: "",
      providertype: "",
    },
  });

  const debouncedServiceSearch = useCallback(
    debounce((value) => {
      value && getProviderServices(value);
    }, 700),
    []
  );

  useEffect(() => {
    getProviderServices();
  }, []);

  useEffect(() => {
    if (!serviceSearch?.trim()) store.dispatch(setAllProviderServices([]));
  }, [serviceSearch]);

  const handleServiceSearchSelect = (selectedOption) => {
    if (!selectedOption) return;
    const match = allProviderServicesList?.find(
      (s) => s.name === selectedOption.name && s.code === selectedOption.code
    );
    if (match) {
      setServiceField((p) => ({
        ...p,
        servicetitle: match.name,
        servicenumber: match.code,
        servicechargetype: match.servicechargetype || "",
      }));
    }
  };

  useEffect(() => {
    if (isEdit && item && opened) {
      form.setValues({
        name: item?.name || "",
        phone: String(item?.phone || ""),
        taxonomy: item?.taxonomy || "",
        address: item?.address || "",
        address2: item?.address2 || "",
        city: item?.city || "",
        state: item?.state || "",
        zipcode: item?.zipcode || "",
        federaltaxtype: item?.federalTaxID?.type || "",
        federaltaxNumber: String(item?.federalTaxID?.number || ""),
        billingIdentifierType: item?.billingIdentifier?.type || "",
        billingIdentifier: (typeof item?.billingIdentifier === "object" ? item.billingIdentifier?.number : item?.billingIdentifier) || "",
        providertype: item?.providertype || "",
      });
      setServices(
        (item?.services || []).map((s) => ({
          servicenumber: s.servicenumber || s.code || "",
          servicetitle: s.servicetitle || s.description || "",
          servicechargetype: s.servicechargetype || s.unit || "",
          servicechargeNumber: s.servicechargeNumber || s.rate || "",
          servicechargeAmount: s.servicechargeAmount || "",
          mod1: s.mod1 || s.modifier || "",
        }))
      );
      setSelectedCode(item?.serviceCode || "");
      setProvider(item?.facilityId?.id || null);
    }
    if (!opened) {
      form.reset();
      setSelectedCode("");
      setServices([]);
      setServiceField(EMPTY_SERVICE);
      setProvider(null);
      setAddressSuggestions([]);
      setShowSuggestions(false);
      setAddr2Suggestions([]);
      setShowSuggestions2(false);
    }
  }, [item, opened]);

  const handleClose = () => {
    if (isEdit) {
      setEditProvider(false);
    } else {
      setState((prev) => ({ ...prev, addProvider: false }));
    }
  };

  const handleAddCode = () => {
    if (!serviceField.servicenumber || !serviceField.servicetitle || !serviceField.servicechargetype) {
      swal({ title: "Please fill Code, Description, and Unit", icon: "warning", dangerMode: true });
      return;
    }
    setServices((prev) => [...prev, { ...serviceField }]);
    setServiceField(EMPTY_SERVICE);
  };

  const handleDeleteService = async (index) => {
    const ok = await swal({
      title: "Delete this service?",
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Delete"],
    });
    if (ok) setServices((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddressSearch = (value) => {
    form.setFieldValue("address", value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value || value.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setAddressLoading(true);
      try {
        const results = await searchAddress(value);
        setAddressSuggestions(results || []);
        setShowSuggestions(true);
      } catch (e) {
        setAddressSuggestions([]);
      }
      setAddressLoading(false);
    }, 400);
  };

  const handleAddress2Search = (value) => {
    form.setFieldValue("address2", value);
    if (debounceRef2.current) clearTimeout(debounceRef2.current);
    if (!value || value.length < 3) { setAddr2Suggestions([]); setShowSuggestions2(false); return; }
    debounceRef2.current = setTimeout(async () => {
      setAddr2Loading(true);
      try {
        const results = await searchAddress(value);
        setAddr2Suggestions(results || []);
        setShowSuggestions2(true);
      } catch (e) { setAddr2Suggestions([]); }
      setAddr2Loading(false);
    }, 400);
  };

  const handleSelectAddress2 = (suggestion) => {
    form.setFieldValue("address2", suggestion.formatted_address);
    setShowSuggestions2(false);
  };

  const handleSelectAddress = async (suggestion) => {
    setShowSuggestions(false);
    setAddressLoading(true);
    try {
      const placeDetails = await getAddressByPlaceId(suggestion.place_id);
      const components = placeDetails?.result?.address_components || [];
      let streetNumber = "", route = "", city = "", addrState = "", zipcode = "";
      components.forEach((comp) => {
        if (comp.types.includes("street_number")) streetNumber = comp.long_name;
        if (comp.types.includes("route")) route = comp.long_name;
        if (comp.types.includes("locality")) city = comp.long_name;
        if (comp.types.includes("administrative_area_level_1")) addrState = comp.short_name;
        if (comp.types.includes("postal_code")) zipcode = comp.long_name;
      });
      const streetAddress = [streetNumber, route].filter(Boolean).join(" ");
      form.setFieldValue("address", streetAddress || suggestion.formatted_address);
      if (city) form.setFieldValue("city", city);
      if (addrState) form.setFieldValue("state", addrState);
      if (zipcode) form.setFieldValue("zipcode", zipcode);
    } catch (e) {
      form.setFieldValue("address", suggestion.formatted_address);
    }
    setAddressLoading(false);
  };

  const onSave = async () => {
    const values = form.values;
    const optionalFields = ["address2", "federaltaxtype", "federaltaxNumber"];
    const missingFields = Object.entries(values)
      .filter(([key]) => !optionalFields.includes(key))
      .filter(([, val]) => !val)
      .map(([key]) => FIELD_LABELS[key] || key);

    if (!(selectedCode?.id || selectedCode)) missingFields.push("Place of Service Name");
    if (!provider) missingFields.push("Facility");
    if (services.length < 1) missingFields.push("Service Codes");

    if (missingFields.length > 0) {
      await swal({
        title: "Please Fill Out The Following Fields",
        text: missingFields.join(", "),
        icon: "warning",
        dangerMode: true,
      });
      return;
    }

    const phoneDigits = (values.phone || "").replace(/\D/g, "");
    if (phoneDigits.length < 10) {
      await swal({ title: "Invalid Phone Number", text: "Please enter a valid  phone number.", icon: "warning", dangerMode: true });
      return;
    }

    const selectedState = values.state?.trim();
    const enteredZipCode = values.zipcode?.trim();
    if (selectedState && enteredZipCode) {
      const entries = stateZipCodes.filter((e) => e.ste_name === selectedState);
      if (!entries.some((e) => e.zip_code === enteredZipCode)) {
        await swal({
          title: "Invalid Zip Code",
          text: `The zip code is not valid for ${selectedState}.`,
          icon: "warning",
          dangerMode: true,
        });
        return;
      }
    }

    const { federaltaxtype, federaltaxNumber, billingIdentifierType, billingIdentifier, ...restValues } = values;

    const payload = {
      ...restValues,
      federalTaxID: { type: federaltaxtype, number: federaltaxNumber },
      billingIdentifier: { type: billingIdentifierType, number: billingIdentifier },
      serviceCode: selectedCode?.id,
      services: services.map((s) => ({
        servicenumber: s.servicenumber,
        servicetitle: s.servicetitle,
        servicechargetype: s.servicechargetype,
        servicechargeNumber: s.servicechargeNumber,
        servicechargeAmount: s.servicechargeAmount,
        mod1: s.mod1,
      })),
      facilityId: isEdit ? provider : provider?.id || provider,
      // admin: profile?.user?.admin ?? profile?.user?.id,
      admin:(profile?.user?.admin && profile?.user?.role) ? profile?.user?.admin : profile?.user?.id,
      createdBy: profile.user.id,
    };

    try {
      if (isEdit) {
        const res = await editProvider({ ...payload, id: item.id });
        if (res?.status === 200) {
          getBillingProvider({ page: 1 });
          onSaved?.();
          swal({ title: "Provider updated successfully", dangerMode: false });
          handleClose();
        }
      } else {
        const res = await createBillingProvider(payload);
        if (res?.status === 200) {
          getBillingProvider({ page: 1 });
          onSaved?.();
          swal({ title: "Provider added successfully", dangerMode: false });
          form.reset();
          handleClose();
        }
      }
    } catch (e) {
      console.log("error", e);
    }
  };

  const stateData = Array.from(new Set(stateZipCodes?.map((o) => o.ste_name)))
    .sort()
    .map((n) => ({ value: n, label: n }));

  return (
    <Drawer
      size="calc(50% + 32px)"
      zIndex={1000}
      withCloseButton={false}
      opened={opened}
      onClose={handleClose}
      position="right"
      overlayProps={{ backgroundOpacity: 0.55, blur: 4 }}
      classNames={{ body: classes.drawerBody, content: classes.drawerContent }}
    >
      {/* Header */}
      <Box className={classes.modalHead}>
        <Box>
          <Text component="p" className={classes.modalEyebrow}>
            Billing Provider
          </Text>
          <Text component="h2" className={classes.modalTitle}>
            {isPreview ? "View Provider" : isEdit ? "Edit Provider" : "Add Provider"}
          </Text>
        </Box>
        <Box component="button" className={classes.closeBtn} onClick={handleClose} aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </Box>
      </Box>

      {/* Body */}
      <Box className={classes.modalBody}>
        <Text component="h6" className={classes.sectionH6}>
          Provider information
        </Text>

        <Box className={classes.formGrid2}>
          <FieldInput label="Provider Type" required>
            <StyledSelect
              placeholder="Type"
              disabled={isPreview}
              data={[
                { value: "facility", label: "Facility" },
                { value: "individual", label: "Individual" },
              ]}
              {...form.getInputProps("providertype")}
            />
          </FieldInput>

          <FieldInput label="Provider Name" required>
            <StyledInput
              placeholder="e.g. Sunrise Home Care"
              disabled={isPreview}
              {...form.getInputProps("name")}
            />
          </FieldInput>

          <FieldInput label="Phone Number" required>
            <StyledInput
              placeholder="+1 (000) 000-0000"
              disabled={isPreview}
              {...form.getInputProps("phone")}
            />
          </FieldInput>

          <FieldInput label="Place of Service Name" required>
            <StyledSelect
              placeholder="e.g. Home (12)"
              searchable
              disabled={isPreview}
              data={(allservicecode?.servicescode ?? [])
                .map((i) => ({ value: i?.id, label: i?.name }))
                .sort((a, b) => a.label.localeCompare(b.label))}
              value={selectedCode?.id || selectedCode}
              onChange={(val) => {
                const found = allservicecode?.servicescode?.find((i) => i.id === val);
                setSelectedCode(found || val);
              }}
            />
          </FieldInput>

          <FieldInput label="Facility" required>
            <StyledSelect
              placeholder="Select facility"
              disabled={isPreview}
              data={
                returnedFacility
                  ? (allfacilty?.allFacilities?.facilities
                      ?.filter((f) => f.id === returnedFacility)
                      ?.map((f) => ({ value: isEdit ? f.id : f, label: f.name })) ?? [])
                  : (allfacilty?.allFacilities?.facilities?.map((f) => ({
                      value: isEdit ? f.id : f,
                      label: f.name,
                    })) ?? [])
              }
              value={provider}
              onChange={(val) => setProvider(val)}
            />
          </FieldInput>

          <FieldInput label="Federal Tax ID" required>
            <Box className={classes.inlineBox}>
              <Select
                className={classes.inlineSelect}
                rightSection={<IconChevronDown color="var(--app-text-muted)" size={14} />}
                variant="unstyled"
                placeholder="EIN"
                disabled={isPreview}
                data={[
                  { value: "EIN", label: "EIN" },
                  { value: "SSN", label: "SSN" },
                ]}
                {...form.getInputProps("federaltaxtype")}
              />
              <TextInput
                className={classes.inlineInput}
                type="text"
                placeholder="00-0000000"
                variant="unstyled"
                disabled={isPreview}
                {...form.getInputProps("federaltaxNumber")}
              />
            </Box>
          </FieldInput>

          <FieldInput label="Taxonomy / Speciality" required>
            <StyledInput
              placeholder="Enter taxonomy"
              disabled={isPreview}
              {...form.getInputProps("taxonomy")}
            />
          </FieldInput>

          <FieldInput label="Billing Identifier" required>
            <Box className={classes.inlineBox}>
              <Select
                className={classes.inlineSelect}
                rightSection={<IconChevronDown color="var(--app-text-muted)" size={14} />}
                variant="unstyled"
                placeholder="Type"
                disabled={isPreview}
                data={[
                  { value: "NPI", label: "NPI" },
                  { value: "ID", label: "ID" },
                ]}
                {...form.getInputProps("billingIdentifierType")}
              />
              <TextInput
                className={classes.inlineInput}
                type="text"
                placeholder="Enter identifier"
                variant="unstyled"
                disabled={isPreview}
                {...form.getInputProps("billingIdentifier")}
              />
            </Box>
          </FieldInput>
        </Box>

        {/* Address */}
        <Text component="h6" className={classes.sectionH6Top}>
          Address
        </Text>

        <Box className={classes.formGrid1}>
          <FieldInput label="Address 1" required>
            <Box style={{ position: "relative" }}>
              <StyledInput
                placeholder="Street address"
                disabled={isPreview}
                value={form.values.address}
                onChange={(e) => handleAddressSearch(e.target.value)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                onFocus={() => addressSuggestions.length > 0 && setShowSuggestions(true)}
                rightSection={addressLoading ? <Loader size="xs" /> : null}
              />
              {showSuggestions && addressSuggestions.length > 0 && (
                <Box
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 1200,
                    background: "var(--app-surface)",
                    border: "1px solid var(--app-border)",
                    borderRadius: 8,
                    maxHeight: 200,
                    overflowY: "auto",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                >
                  {addressSuggestions.map((s, i) => (
                    <Box
                      key={s.place_id || i}
                      style={{ padding: "10px 14px", cursor: "pointer", fontSize: 14 }}
                      sx={{ "&:hover": { background: "var(--app-surface-2)" } }}
                      onMouseDown={() => handleSelectAddress(s)}
                    >
                      {s.formatted_address}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </FieldInput>
          <FieldInput label="Address 2">
            <Box style={{ position: "relative" }}>
              <StyledInput
                disabled={isPreview}
                value={form.values.address2}
                onChange={(e) => handleAddress2Search(e.target.value)}
                onBlur={() => setTimeout(() => setShowSuggestions2(false), 200)}
                onFocus={() => addr2Suggestions.length > 0 && setShowSuggestions2(true)}
                rightSection={addr2Loading ? <Loader size="xs" /> : null}
              />
              {showSuggestions2 && addr2Suggestions.length > 0 && (
                <Box style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1200, background: "var(--app-surface)", border: "1px solid var(--app-border)", borderRadius: 8, maxHeight: 200, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
                  {addr2Suggestions.map((s, i) => (
                    <Box key={s.place_id || i} style={{ padding: "10px 14px", cursor: "pointer", fontSize: 14 }} sx={{ "&:hover": { background: "var(--app-surface-2)" } }} onMouseDown={() => handleSelectAddress2(s)}>
                      {s.formatted_address}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </FieldInput>
        </Box>

        <Box className={classes.formGrid3}>
          <FieldInput label="City" required>
            <StyledInput disabled={isPreview} {...form.getInputProps("city")} />
          </FieldInput>
          <FieldInput label="State" required>
            <StyledSelect searchable disabled={isPreview} data={stateData} {...form.getInputProps("state")} />
          </FieldInput>
          <FieldInput label="Zip Code" required>
            <StyledInput type="text" disabled={isPreview} {...form.getInputProps("zipcode")} />
          </FieldInput>
        </Box>

        {/* Service codes & rates */}
        <Box className={classes.sectionServiceRow}>
          <Box className={classes.sectionServiceLeft}>
            <Text component="h6" className={classes.sectionH6Inline}>
              Service codes &amp; rates
            </Text>
            <Text className={classes.sectionDescInline}>
              HCPCS / CPT codes you bill and the rate per unit. Override rates per payer in their detail page.
            </Text>
          </Box>
          {!isPreview && (
            <Box component="button" type="button" className={classes.lookupBtn} onClick={() => setOpenLookup(true)}>
              <LuSearch size={14} />
              Lookup Rates
            </Box>
          )}
        </Box>

        {!isPreview && (
          <Select
            searchable
            clearable
            placeholder="Search service # or Service Title"
            icon={<LuSearch size={14} color="var(--app-text-muted)" />}
            rightSection={<IconChevronDown size={14} color="var(--app-text-muted)" />}
            rightSectionWidth={28}
            styles={{
              input: { borderRadius: "8px", border: "1px solid var(--app-border)", fontSize: "13px", height: "40px" },
              rightSection: { pointerEvents: "none" },
            }}
            mb={12}
            data={(allProviderServicesList || []).map((item) => ({ value: item, label: `${item.code} - ${item.name}` }))}
            onChange={handleServiceSearchSelect}
            onSearchChange={(val) => { setServiceSearch(val); debouncedServiceSearch(val); }}
          />
        )}

        {!isPreview && (
          <>
            <Box className={classes.formGrid2}>
              <FieldInput label="Code" required>
                <StyledInput
                  placeholder="e.g. T1019"
                  value={serviceField.servicenumber}
                  onChange={(e) => setServiceField((p) => ({ ...p, servicenumber: e.target.value }))}
                />
              </FieldInput>
              <FieldInput label="Description" required>
                <StyledInput
                  placeholder="e.g. Personal care services, per 15 minutes"
                  value={serviceField.servicetitle}
                  onChange={(e) => setServiceField((p) => ({ ...p, servicetitle: e.target.value }))}
                />
              </FieldInput>
            </Box>

            <Box className={classes.formGrid3}>
              <FieldInput label="Unit" required>
                <StyledSelect
                  placeholder="Select unit"
                  data={UNIT_OPTIONS}
                  value={serviceField.servicechargetype}
                  onChange={(val) => setServiceField((p) => ({ ...p, servicechargetype: val }))}
                />
              </FieldInput>
              <FieldInput label="Rate">
                <StyledInput
                  placeholder="0.00"
                  value={serviceField.servicechargeNumber}
                  onChange={(e) => setServiceField((p) => ({ ...p, servicechargeNumber: e.target.value }))}
                />
              </FieldInput>
              <FieldInput label="Default modifier">
                <StyledInput
                  placeholder="e.g. U2"
                  value={serviceField.mod1}
                  onChange={(e) => setServiceField((p) => ({ ...p, mod1: e.target.value }))}
                />
              </FieldInput>
            </Box>

            <Box
              component="button"
              type="button"
              className={classes.addCodeBtn}
              onClick={handleAddCode}
            >
              + Add code
            </Box>
          </>
        )}

        {services.length > 0 && (
          <Box className={classes.tableWrap}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Description</th>
                  <th>Unit</th>
                  <th>Rate</th>
                  <th>Default modifier</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {services.map((svc, i) => (
                  <tr key={i}>
                    <td>
                      <input
                        className={classes.tableInput}
                        value={svc.servicenumber}
                        disabled={isPreview}
                        onChange={(e) =>
                          setServices((prev) =>
                            prev.map((s, idx) =>
                              idx === i ? { ...s, servicenumber: e.target.value } : s
                            )
                          )
                        }
                      />
                    </td>
                    <td>{svc.servicetitle}</td>
                    <td>{svc.servicechargetype}</td>
                    <td>
                      <input
                        className={classes.tableInput}
                        value={svc.servicechargeNumber}
                        disabled={isPreview}
                        onChange={(e) =>
                          setServices((prev) =>
                            prev.map((s, idx) =>
                              idx === i ? { ...s, servicechargeNumber: e.target.value } : s
                            )
                          )
                        }
                      />
                    </td>
                    <td>
                      <input
                        className={classes.tableInput}
                        value={svc.mod1 ?? ""}
                        disabled={isPreview}
                        onChange={(e) =>
                          setServices((prev) =>
                            prev.map((s, idx) =>
                              idx === i ? { ...s, mod1: e.target.value } : s
                            )
                          )
                        }
                      />
                    </td>
                    <td className={classes.tableDeleteCell}>
                      {!isPreview && (
                        <Box component="button" type="button" className={classes.deleteBtn} onClick={() => handleDeleteService(i)}>
                          <LuTrash2 size={14} />
                        </Box>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box className={classes.modalFoot}>
        <Box component="button" type="button" className={classes.cancelBtn} onClick={handleClose}>
          {isPreview ? "Close" : "Cancel"}
        </Box>
        {!isPreview && (
          <Box component="button" type="button" className={classes.saveBtn} onClick={onSave}>
            {isEdit ? "Save changes" : "Save provider"}
          </Box>
        )}
      </Box>

      <ProviderServicesLookupModal
        openlookup={openlookup}
        setOpenLookup={setOpenLookup}
        setProviderServices={(svc) =>
          setServiceField({
            servicenumber: svc.servicenumber || "",
            servicetitle: svc.servicetitle || "",
            servicechargetype: svc.servicechargetype || "",
            servicechargeNumber: svc.servicechargeNumber || "",
            servicechargeAmount: svc.servicechargeAmount || "",
            mod1: "",
          })
        }
      />
    </Drawer>
  );
}

export default AddProvider;
